import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';

import {
    InstallerCompaniesApi,
    type UnallocatedInstallationDeviceUpdateModel,
    UnallocatedInstallationsAddGatewayDeviceRequest,
    UnallocatedInstallationsApi,
    UnallocatedInstallationsGetUnallocatedInstallationsRequest,
    UnallocatedInstallationsScanGatewayDevicesRequest,
    UnallocatedInstallationsUpdateDeviceConfigurationRequest,
} from '@swagger-http';

import useToastList from '@components/notifications/toast-notifications-provider';
import { createRequestConfiguration } from '@tools/utils/authentication';

export const useAddUnallocatedInstallation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['addUnallocatedInstallation'],
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['getUnallocatedInstallations'],
            });
        },
        mutationFn: async (
            payload: UnallocatedInstallationsAddGatewayDeviceRequest,
        ) => {
            const response = new UnallocatedInstallationsApi(
                createRequestConfiguration(),
            ).unallocatedInstallationsAddGatewayDevice(payload);

            return response;
        },
    });
};

export const useGetUnallocatedInstallations = (
    {
        page,
        limit,
        tenantId,
        installerId,
        startCode,
    }: UnallocatedInstallationsGetUnallocatedInstallationsRequest,
    enabled?: boolean,
) =>
    useQuery({
        queryKey: [
            'getUnallocatedInstallations',
            page,
            limit,
            tenantId,
            installerId,
            startCode,
        ],
        enabled,
        queryFn: async () => {
            const response = new UnallocatedInstallationsApi(
                createRequestConfiguration(),
            ).unallocatedInstallationsGetUnallocatedInstallations({
                page,
                limit,
                tenantId,
                installerId,
                startCode,
            });

            return response;
        },
    });

export const UNALLOCATED_DEVICES_QUERY_KEY = (installation: string) => [
    'getUnallocatedInstallationDevices',
    installation,
];

export const useGetUnallocatedInstallationDevices = (installation: string) => {
    const { t } = useTranslation();
    const { showToast } = useToastList();

    const query = useQuery({
        queryKey: UNALLOCATED_DEVICES_QUERY_KEY(installation),
        queryFn: async () => {
            try {
                const api = new UnallocatedInstallationsApi(
                    createRequestConfiguration(),
                );

                const response =
                    await api.unallocatedInstallationsGetUnallocatedInstallationDevices(
                        {
                            installation,
                        },
                    );

                if (!response) {
                    showToast({
                        toastType: 'error',
                        message: t('unallocatedInstallation.requestFailed'),
                    });
                    throw new Error(`API request failed`);
                }

                return response;
            } catch (error) {
                if (error instanceof Error) {
                    showToast({
                        toastType: 'error',
                        message: t('unallocatedInstallation.fetchFailed'),
                    });
                    throw new Error(
                        `Failed to fetch installation data: ${error.message}`,
                    );
                }

                showToast({
                    toastType: 'error',
                    message: t('unallocatedInstallation.unknownError'),
                });

                throw new Error(
                    'An unknown error occurred while fetching installation data',
                );
            }
        },
    });

    return {
        data: query.data,
        refetch: query.refetch,
    };
};

export const useDeleteUnallocatedInstallation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['deleteUnallocatedInstallation'],
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['getUnallocatedInstallations'],
            });
        },
        mutationFn: async ({
            configurationId,
        }: {
            configurationId: string;
        }) => {
            const response = await new UnallocatedInstallationsApi(
                createRequestConfiguration(),
            ).unallocatedInstallationsDeleteInstallation({
                installationId: configurationId,
            });

            return response;
        },
    });
};

export const useAssignCustomerToUnallocatedInstallation = (email: string) => {
    const queryClient = useQueryClient();
    const { showToast } = useToastList();

    return useMutation({
        mutationKey: ['assignCustomerUnallocatedInstallation'],
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['getUnallocatedInstallations'],
            });
        },
        mutationFn: async ({
            configurationId,
        }: {
            configurationId: string;
        }) => {
            try {
                const response = await new UnallocatedInstallationsApi(
                    createRequestConfiguration(),
                ).unallocatedInstallationsAssignToCustomer({
                    installationId: configurationId,
                    assignToCustomerRequestBody: {
                        customer: email,
                    },
                });

                showToast({
                    toastType: 'success',
                    message: t(
                        'unallocatedInstallation.assignCustomerDialog.feedback.notification.success',
                    ),
                });

                return response;
            } catch (error) {
                showToast({
                    toastType: 'error',
                    message: t(
                        'unallocatedInstallation.assignCustomerDialog.feedback.notification.error',
                    ),
                });
                throw error;
            }
        },
    });
};

export const useInstallerCompanies = () => {
    // Temporary workaround due to the lack of pagination or a 'no-limit' option in the API.
    // It's currently not possible to fetch more than 100 items without manually increasing the limit.
    // TODO: Once the backend supports a no-limit option, this should be reverted to avoid hardcoding a fetch limit.
    const MAX_COMPANY_FETCH_LIMIT = 100;

    return useQuery({
        queryKey: ['getUnallocatedInstallationCompanies'],
        queryFn: async () => {
            const response = new InstallerCompaniesApi(
                createRequestConfiguration(),
            ).installerCompaniesGetInstallerCompanies({
                limit: MAX_COMPANY_FETCH_LIMIT,
            });

            return response;
        },
    });
};

export const useUpdateUnallocatedInstallation = (
    installationId: string,
    id: string,
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['updateUnallocatedInstallation', installationId],
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['getUnallocatedInstallationDevices', installationId],
            });
        },
        mutationFn: async (
            unallocatedInstallationDeviceUpdateModel: UnallocatedInstallationDeviceUpdateModel,
        ) => {
            const requestParameters: UnallocatedInstallationsUpdateDeviceConfigurationRequest =
                {
                    installationId,
                    id,
                    unallocatedInstallationDeviceUpdateModel,
                };

            const response = await new UnallocatedInstallationsApi(
                createRequestConfiguration(),
            ).unallocatedInstallationsUpdateDeviceConfiguration(
                requestParameters,
            );

            return response;
        },
    });
};

export const useRescanUnallocatedInstallation = (id: string) => {
    const queryClient = useQueryClient();
    const { showToast, removeToast } = useToastList();

    return useMutation({
        mutationKey: ['rescanUnallocatedInstallation'],

        mutationFn: async (
            payload: UnallocatedInstallationsScanGatewayDevicesRequest,
        ) => {
            try {
                const api = new UnallocatedInstallationsApi(
                    createRequestConfiguration(),
                );

                return await api.unallocatedInstallationsScanGatewayDevices(
                    payload,
                );
            } catch (error: any) {
                if (error?.response?.text) {
                    const message = await error.response.text();

                    // remove any toasts with this message, to avoid showing dup errors
                    removeToast(message);

                    showToast({ key: message, toastType: 'error', message });
                }
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: UNALLOCATED_DEVICES_QUERY_KEY(id),
            });
        },
    });
};

export const useLiveStreamUnallocatedInstallation = (
    installationId: string,
    enabled?: boolean,
) => {
    const { showToast } = useToastList();
    const queryInfo = useQuery({
        queryKey: ['getUnallocatedInstallationsGetLiveData', installationId],
        enabled,
        queryFn: async () =>
            await new UnallocatedInstallationsApi(
                createRequestConfiguration(),
            ).unallocatedInstallationsGetLiveData({ installationId }),
    });

    useEffect(() => {
        if (queryInfo.data) {
            showToast({
                key: 'liveStreamSuccess',
                toastType: 'success',
                message: t(
                    'unallocatedInstallations.liveStreamTab.status.success',
                ),
            });
        }
    }, [queryInfo.data, showToast]);

    return { ...queryInfo };
};
